/** SUNTRONIX ONE — Configuration Studio (admin). */
import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Settings, Search, ShieldAlert, ListChecks, LayoutGrid, FileText,
  History, Save, RotateCcw, RefreshCw, User, Package, Wrench, Sparkles,
} from "lucide-react";

const TABS = [
  { id: "quotation",             label: "Quotations",       icon: FileText,     kind: "settings" },
  { id: "product",                  label: "Products",           icon: Package,       kind: "settings" },
  { id: "service",                  label: "Services",           icon: Wrench,          kind: "settings" },
  { id: "customer",                label: "Customers",         icon: User,                 kind: "settings" },
  { id: "numbering",             label: "Numbering",         icon: FileText,     kind: "settings" },
  { id: "pdf_template",     label: "PDF Templates", icon: FileText,     kind: "settings" },
  { id: "dashboard",             label: "Dashboards",       icon: LayoutGrid, kind: "settings" },
  { id: "navigation",           label: "Navigation",       icon: LayoutGrid, kind: "settings" },
  { id: "role",                          label: "Roles",                 icon: ShieldAlert, kind: "settings" },
  { id: "email_notification", label: "Notifications", icon: FileText, kind: "settings" },
  { id: "country",                  label: "Country",             icon: LayoutGrid, kind: "settings" },
  { id: "picklists",             label: "Picklists",         icon: ListChecks, kind: "picklists" },
  { id: "modules",                  label: "Modules",             icon: LayoutGrid, kind: "modules" },
  { id: "history",                  label: "History",             icon: History,       kind: "history" },
];

export default function ConfigStudio() {
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [sp, setSp] = useSearchParams();
  const rawTab = sp.get("tab") || "quotation";
  const tab = TABS.some((t) => t.id === rawTab) ? rawTab : "quotation";

  useEffect(() => {
    api.get("/config-studio/meta").then(({ data }) => setMeta(data))
        .catch(() => toast.error("Load failed"));
  }, []);

  useEffect(() => {
    if (!q.trim()) { setHits([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/config-studio/search",
                                                                    { params: { q: q.trim() } });
        setHits(data.items || []);
      } catch { setHits([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const canAdmin = meta?.current_user?.can_admin;

  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="config-studio">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-slate-900 text-white grid place-items-center">
            <Settings className="h-5 w-5" strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-xl font-heading font-semibold text-slate-900">
              Configuration Studio
            </div>
            <div className="text-[11px] text-slate-500">
              Governed no-code layer · every change is audited &amp; reversible
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/administration/rules"
              data-testid="cs-open-rules"
              className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
            <Settings className="h-3.5 w-3.5" /> Advanced Rules
          </Link>
          <Link to="/administration/process-studio"
              data-testid="cs-open-process-studio"
              className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
            <Settings className="h-3.5 w-3.5" /> Process Studio
          </Link>
          <Link to="/admin/communications"
              data-testid="cs-open-communications"
              className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
            <Settings className="h-3.5 w-3.5" /> Communications
          </Link>
          <Link to="/admin/pdf-style"
              data-testid="cs-open-pdf-style"
              className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
            <Settings className="h-3.5 w-3.5" /> PDF Style
          </Link>
          <Link to="/admin/quotation-template"
              data-testid="cs-open-quotation-template"
              className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
            <FileText className="h-3.5 w-3.5" /> Quotation Template
          </Link>
          <Link to="/admin/bank-details"
              data-testid="cs-open-bank-details"
              className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
            <FileText className="h-3.5 w-3.5" /> Bank Details
          </Link>
          <Link to="/admin/customer-prefix-cleanup"
              data-testid="cs-open-prefix-cleanup"
              className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
            <FileText className="h-3.5 w-3.5" /> Prefix Cleanup
          </Link>
          <Link to="/admin/crm-automation"
              data-testid="cs-open-crm-automation"
              className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
            <Sparkles className="h-3.5 w-3.5" /> CRM Automation
          </Link>
          <Link to="/admin/moments-studio"
              data-testid="cs-open-moments-studio"
              className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
            <Sparkles className="h-3.5 w-3.5" /> Moments Studio
          </Link>
          <Link to="/profile/my-settings"
              data-testid="cs-open-my-settings"
              className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
            <User className="h-3.5 w-3.5" /> My settings
          </Link>
        </div>
      </header>

      {!canAdmin && (
        <div className="border border-amber-200 bg-amber-50/40 rounded-lg p-3 text-[12px] text-amber-800">
          You have <b>read-only</b> access. Only super_admin can save changes here.
        </div>
      )}

      <div className="relative max-w-[520px]">
        <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search settings, picklists, modules…"
            data-testid="cs-search"
            className="w-full pl-8 pr-3 py-1.5 text-[12px] border border-slate-200 rounded-md focus:outline-none focus:border-slate-400" />
        {hits.length > 0 && (
          <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-80 overflow-y-auto">
            {hits.map((h) => (
              <Link key={`${h.kind}-${h.key}-${h.area}`} to={h.path}
                  onClick={() => { setQ(""); setHits([]); }}
                  className="block px-3 py-1.5 text-[11px] hover:bg-slate-50 border-b border-slate-100">
                <span className="text-[9px] uppercase tracking-wider text-slate-500">{h.kind}</span>
                <span className="ml-2 font-medium">{h.label}</span>
                {h.area && <span className="ml-2 text-slate-400">· {h.area}</span>}
              </Link>
            ))}
          </div>
        )}
      </div>

      <nav className="border-b border-slate-200 flex items-center gap-1 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id}
              onClick={() => setSp({ tab: t.id }, { replace: true })}
              data-testid={`cs-tab-${t.id}`}
              className={"px-3 py-2 text-[12px] font-medium border-b-2 -mb-[1px] flex items-center gap-1.5 whitespace-nowrap " +
                  (tab === t.id
                      ? "border-slate-900 text-slate-900"
                      : "border-transparent text-slate-500 hover:text-slate-800")}>
            <t.icon className="h-3.5 w-3.5" strokeWidth={1.6} />
            {t.label}
          </button>
        ))}
      </nav>

      {TABS.find((t) => t.id === tab)?.kind === "settings" &&
          <SettingsPane area={tab} canAdmin={canAdmin} />}
      {tab === "picklists" && <PicklistsPane canAdmin={canAdmin} />}
      {tab === "modules"    && <ModulesPane canAdmin={canAdmin} />}
      {tab === "history"    && <HistoryPane />}
    </div>
  );
}

/* ---------- SETTINGS PANE ------------------------------------------------- */
function SettingsPane({ area, canAdmin }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ key: "", value: "", reason: "" });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/config-studio/settings",
                                                                    { params: { area } });
      setRows(data.items || []);
    } catch { toast.error("Load failed"); }
    finally { setBusy(false); }
  }, [area]);
  useEffect(() => { load(); }, [area, load]);

  const save = async () => {
    if (!form.key.trim()) return toast.error("Key required");
    try {
      let parsed = form.value;
      // Try JSON parse for numbers, arrays, objects, booleans
      const trimmed = String(form.value).trim();
      if (/^(true|false|-?\d+(\.\d+)?|\[|\{|")/.test(trimmed)) {
        try { parsed = JSON.parse(trimmed); } catch { /* keep as string */ }
      }
      await api.put("/config-studio/settings",
          { area, key: form.key.trim(), value: parsed,
            reason: form.reason || undefined });
      toast.success("Setting saved");
      setForm({ key: "", value: "", reason: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const restore = async (key) => {
    try {
      await api.post(`/config-studio/settings/${area}/${key}/restore`);
      toast.success("Restored previous value");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  };

  return (
    <div className="space-y-3" data-testid={`cs-pane-${area}`}>
      {canAdmin && (
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
          <div className="text-[11px] font-semibold text-slate-900 mb-2">Add / update setting</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-[11px]">
            <input value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })}
                placeholder="setting_key (e.g. default_validity_days)"
                data-testid="cs-setting-key"
                className="border border-slate-200 rounded px-2 py-1 md:col-span-1" />
            <input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
                placeholder="value (JSON or plain)"
                data-testid="cs-setting-value"
                className="border border-slate-200 rounded px-2 py-1 md:col-span-2" />
            <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="reason (optional)"
                className="border border-slate-200 rounded px-2 py-1" />
          </div>
          <div className="flex justify-end mt-2">
            <button onClick={save} data-testid="cs-setting-save"
                className="text-[11px] px-3 py-1 rounded bg-slate-900 text-white inline-flex items-center gap-1">
              <Save className="h-3 w-3" /> Save
            </button>
          </div>
        </div>
      )}

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Key</th>
              <th className="text-left px-3 py-2">Value</th>
              <th className="text-left px-3 py-2">Previous</th>
              <th className="text-left px-3 py-2">Updated</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !busy && (
              <tr><td colSpan={5} className="text-center py-6 text-slate-400">
                No settings yet in <b>{area}</b>.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-slate-100"
                data-testid={`cs-row-${r.key}`}>
                <td className="px-3 py-2 font-mono text-[11px]">{r.key}</td>
                <td className="px-3 py-2 font-mono text-[11px]">
                  {typeof r.value === "object" ? JSON.stringify(r.value) : String(r.value)}
                </td>
                <td className="px-3 py-2 text-slate-500 font-mono text-[10px]">
                  {r.previous_value == null ? "—"
                    : typeof r.previous_value === "object"
                        ? JSON.stringify(r.previous_value)
                        : String(r.previous_value)}
                </td>
                <td className="px-3 py-2 text-slate-500 text-[10px]">
                  {r.updated_at?.slice(0, 19).replace("T", " ")}<br/>
                  <span className="text-slate-400">{r.updated_by}</span>
                </td>
                <td className="px-3 py-2 text-right">
                  {canAdmin && r.previous_value != null && (
                    <button onClick={() => restore(r.key)}
                        data-testid={`cs-restore-${r.key}`}
                        className="text-[10px] px-2 py-0.5 border border-slate-200 rounded inline-flex items-center gap-1 hover:bg-slate-50">
                      <RotateCcw className="h-3 w-3" /> Restore
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- PICKLISTS ------------------------------------------------------ */
function PicklistsPane({ canAdmin }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ picklist_key: "", label: "", values: "" });
  const load = async () => {
    try {
      const { data } = await api.get("/config-studio/picklists");
      setRows(data.items || []);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.picklist_key.trim() || !form.label.trim())
      return toast.error("Key and label required");
    try {
      const values = form.values.split("\n").filter((v) => v.trim()).map((line, i) => {
        const [value, label] = line.split("|").map((s) => s.trim());
        return { value, label: label || value, sort: i, active: true };
      });
      await api.put("/config-studio/picklists",
          { picklist_key: form.picklist_key.trim(),
            label: form.label.trim(), values });
      toast.success("Picklist saved");
      setForm({ picklist_key: "", label: "", values: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="space-y-3" data-testid="cs-pane-picklists">
      {canAdmin && (
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
          <div className="text-[11px] font-semibold text-slate-900 mb-2">New / update picklist</div>
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <input value={form.picklist_key} onChange={(e) => setForm({ ...form, picklist_key: e.target.value })}
                placeholder="picklist_key (e.g. industry)"
                data-testid="cs-pl-key"
                className="border border-slate-200 rounded px-2 py-1 font-mono" />
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Display label"
                className="border border-slate-200 rounded px-2 py-1" />
            <textarea value={form.values} onChange={(e) => setForm({ ...form, values: e.target.value })}
                rows={4} placeholder="One per line — value|label"
                data-testid="cs-pl-values"
                className="col-span-2 border border-slate-200 rounded px-2 py-1 font-mono text-[11px]" />
          </div>
          <div className="flex justify-end mt-2">
            <button onClick={save} data-testid="cs-pl-save"
                className="text-[11px] px-3 py-1 rounded bg-slate-900 text-white inline-flex items-center gap-1">
              <Save className="h-3 w-3" /> Save picklist
            </button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.length === 0 && (
          <div className="col-span-2 text-center py-8 text-slate-400 text-[12px]">
            No picklists yet. {canAdmin && "Create one above."}
          </div>
        )}
        {rows.map((p) => (
          <div key={p.picklist_key} className="border border-slate-200 rounded-lg p-3"
            data-testid={`cs-pl-${p.picklist_key}`}>
            <div className="text-[12px] font-heading font-semibold text-slate-900">{p.label}</div>
            <div className="text-[10px] text-slate-400 font-mono mb-2">{p.picklist_key}</div>
            <ul className="text-[11px] space-y-0.5">
              {(p.values || []).slice(0, 10).map((v) => (
                <li key={v.value} className="flex items-center justify-between border-b border-slate-100 py-0.5">
                  <span className="text-slate-700">{v.label}</span>
                  <span className={"text-[9px] uppercase " + (v.active ? "text-emerald-600" : "text-rose-500")}>
                    {v.active ? "Active" : "Inactive"}
                  </span>
                </li>
              ))}
              {p.values?.length > 10 && <li className="text-slate-400">+{p.values.length - 10} more</li>}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- MODULES -------------------------------------------------------- */
function ModulesPane({ canAdmin }) {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/config-studio/modules"); setRows(data.items || []); }
    catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (m, field, value) => {
    try {
      await api.patch("/config-studio/modules",
          { module_key: m.module_key, [field]: value });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="cs-pane-modules">
      <table className="w-full text-[12px]">
        <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
          <tr>
            <th className="text-left px-3 py-2">Module</th>
            <th className="text-left px-3 py-2">Display name</th>
            <th className="text-center px-3 py-2">Nav order</th>
            <th className="text-center px-3 py-2">Visible</th>
            <th className="text-center px-3 py-2">Active</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.module_key} className="border-t border-slate-100"
              data-testid={`cs-module-${m.module_key}`}>
              <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{m.module_key}</td>
              <td className="px-3 py-2 font-medium text-slate-900">{m.display_name}</td>
              <td className="px-3 py-2 text-center">{m.nav_order}</td>
              <td className="px-3 py-2 text-center">
                <input type="checkbox" disabled={!canAdmin}
                    checked={!!m.nav_visible}
                    onChange={(e) => toggle(m, "nav_visible", e.target.checked)}
                    data-testid={`cs-module-vis-${m.module_key}`} />
              </td>
              <td className="px-3 py-2 text-center">
                <input type="checkbox" disabled={!canAdmin}
                    checked={m.active !== false}
                    onChange={(e) => toggle(m, "active", e.target.checked)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- HISTORY -------------------------------------------------------- */
function HistoryPane() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/config-studio/history?limit=200"); setRows(data.items || []); }
    catch { toast.error("Load failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-2" data-testid="cs-pane-history">
      <button onClick={load}
          className="text-[11px] border border-slate-200 rounded px-2 py-1 inline-flex items-center gap-1">
        <RefreshCw className={"h-3 w-3 " + (busy ? "animate-spin" : "")} /> Refresh
      </button>
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500 text-[9px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">When</th>
              <th className="text-left px-3 py-2">Actor</th>
              <th className="text-left px-3 py-2">Action</th>
              <th className="text-left px-3 py-2">Area · Key</th>
              <th className="text-left px-3 py-2">Before</th>
              <th className="text-left px-3 py-2">After</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-1.5 font-mono text-slate-500">{r.at?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-3 py-1.5">{r.actor}</td>
                <td className="px-3 py-1.5 font-medium">{r.action}</td>
                <td className="px-3 py-1.5">{r.area} · <span className="font-mono">{r.key || "—"}</span></td>
                <td className="px-3 py-1.5 font-mono text-slate-500">
                  {r.before == null ? "—"
                    : typeof r.before === "object" ? JSON.stringify(r.before).slice(0, 60) : String(r.before)}
                </td>
                <td className="px-3 py-1.5 font-mono">
                  {r.after == null ? "—"
                    : typeof r.after === "object" ? JSON.stringify(r.after).slice(0, 60) : String(r.after)}
                </td>
              </tr>
            ))}
            {rows.length === 0 && !busy && (
              <tr><td colSpan={6} className="text-center py-6 text-slate-400">
                No configuration history yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
